import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X } from 'lucide-react';

const navItems = [
  { name: 'Home', path: '/' },
  { name: 'AI Consulting', path: '/ai-consulting' },
  { name: 'AI Creation', path: '/ai-creation' },
  { name: 'Media & Brand', path: '/media-brand' },
  { name: 'Sectors', path: '/sectors' },
  { name: 'Work', path: '/work' },
  { name: 'Team', path: '/team' },
  { name: 'Contact', path: '/contact' },
];

const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const menuVariants = {
    closed: {
      clipPath: 'circle(0% at calc(100% - 40px) 40px)',
      transition: {
        type: 'spring',
        stiffness: 400,
        damping: 40,
      },
    },
    open: {
      clipPath: 'circle(150% at calc(100% - 40px) 40px)',
      transition: {
        type: 'spring',
        stiffness: 20,
        restDelta: 2,
      },
    },
  };

  const itemVariants = {
    closed: { opacity: 0, y: 50 },
    open: (i) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: i * 0.1,
        duration: 0.5,
        ease: [0.22, 1, 0.36, 1],
      },
    }),
  };

  return (
    <>
      {/* Main Navigation Bar */}
      <header
        className={`fixed top-0 left-0 w-full z-40 transition-colors duration-300 ${
          scrolled ? 'bg-elate-black/90 backdrop-blur-sm' : 'bg-transparent'
        }`}
        data-testid="main-navigation"
      >
        <div className="container-custom flex justify-between items-center py-6">
          <Link to="/" className="relative z-50" data-testid="logo-link">
            <img
              src="/images/logo.png"
              alt="Elate Global"
              className="h-10 md:h-12 w-auto"
            />
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-6">
            {navItems.slice(0, 7).map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`link-hover text-sm uppercase tracking-wider font-semibold ${
                  location.pathname === item.path ? 'text-elate-yellow' : 'text-white'
                }`}
                data-testid={`nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {item.name}
              </Link>
            ))}
            <Link
              to="/contact"
              className="btn-primary text-sm py-3 px-6"
              data-testid="nav-contact-btn"
            >
              Get in Touch
            </Link>
          </nav>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="lg:hidden relative z-50 p-2 text-white"
            aria-label={isOpen ? 'Close menu' : 'Open menu'}
            data-testid="mobile-menu-toggle"
          >
            {isOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>
      </header>

      {/* Full Screen Overlay Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            className="fixed inset-0 bg-elate-yellow z-40 flex flex-col justify-center items-center"
            initial="closed"
            animate="open"
            exit="closed"
            variants={menuVariants}
            data-testid="mobile-menu-overlay"
          >
            <nav className="flex flex-col items-center gap-6">
              {navItems.map((item, i) => (
                <motion.div
                  key={item.path}
                  custom={i}
                  variants={itemVariants}
                  initial="closed"
                  animate="open"
                  exit="closed"
                >
                  <Link
                    to={item.path}
                    className={`text-4xl md:text-6xl font-heading uppercase tracking-tight ${
                      location.pathname === item.path ? 'text-elate-black' : 'text-elate-black/70'
                    } hover:text-elate-black transition-colors`}
                    data-testid={`mobile-nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {item.name}
                  </Link>
                </motion.div>
              ))}
            </nav>

            <motion.div
              className="absolute bottom-12 flex gap-8"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.8 }}
            >
              <a
                href="https://x.com/elateglobal"
                target="_blank"
                rel="noopener noreferrer"
                className="text-elate-black uppercase tracking-wider text-sm font-semibold hover:opacity-70 transition-opacity"
              >
                X
              </a>
              <a
                href="https://instagram.com/elateglobal"
                target="_blank"
                rel="noopener noreferrer"
                className="text-elate-black uppercase tracking-wider text-sm font-semibold hover:opacity-70 transition-opacity"
              >
                Instagram
              </a>
              <a
                href="mailto:info@elate.global"
                className="text-elate-black uppercase tracking-wider text-sm font-semibold hover:opacity-70 transition-opacity"
              >
                Email
              </a>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navigation;
